import React, { useState, useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { colors } from "style";
import { computeSellingPriceFromMargin, formatCurrency } from "utils";
import { useMultiCurrencyConversions } from "hooks";
import { CURRENCIES, RATE_FREQUENCIES } from "constants/index";
import Card from "components/Card";
import { Spinner } from "components/Loader";
import { Col, Row } from "components/Containers";
import { ICON_POSITION, ICON_SIZE, ICON_TYPE, withIcon } from "components/Icon";
import { TextInput } from "components/Form";
import { SecondaryButton } from "components/Buttons";

const DEFAULT_MARGIN = 25;
const TARGET_CURRENCIES = CURRENCIES.map((c) => c.value);

/**
 * ApplicantMarginCalculator
 *
 * @param {Object}    data
 * @param {Boolean}   loading
 * @param {Function}  onValueSelect
 */
const ApplicantMarginCalculator = ({ data, loading, onValueSelect }) => {
  const [buyingCurrency, setBuyingCurrency] = useState();
  const [margin, setMargin] = useState();
  const [buyingRate, setBuyingRate] = useState();

  useEffect(() => {
    if (data?.negotiatedRate) {
      setBuyingRate(data.negotiatedRate.amount);
      setBuyingCurrency(data.negotiatedRate.currency);
      setMargin(DEFAULT_MARGIN);
    }
  }, [data]);

  const { data: currencyData, loading: currencyLoading } = useMultiCurrencyConversions({
    from: buyingCurrency,
    toList: TARGET_CURRENCIES,
    amount: 1,
  });

  const sellingRates = useMemo(() => {
    if (!buyingRate || !margin || !Object.keys(currencyData).length) return {};

    const baseSellingRate = computeSellingPriceFromMargin(buyingRate, margin);

    return TARGET_CURRENCIES.reduce((acc, currency) => {
      const rate = currencyData[currency]?.conversionRate;
      const finalRate = rate ? baseSellingRate * rate : baseSellingRate;

      return {
        ...acc,
        [currency]: {
          amount: finalRate.toFixed(2),
          currency,
          frequency: RATE_FREQUENCIES.hourly,
        },
      };
    }, {});
  }, [buyingRate, margin, currencyData]);

  return (
    <Card css={styles.container}>
      <Col gap="1rem">
        {loading ? (
          <Spinner size="1.5rem" />
        ) : (
          <Row css={styles.title}>
            <span>
              <FormattedMessage id="Missions.Applicants.ApplicantMarginCalculator.Title" />
            </span>
            <Row>
              <div css={styles.form_item}>
                <MarginInput
                  placeholder="Margin"
                  value={margin}
                  onChange={(_name, newMargin) => setMargin(newMargin)}
                  disabled={!buyingRate}
                />
              </div>
              {CURRENCIES.map((curr) => {
                const sellingData = sellingRates[curr.value];

                return (
                  <SecondaryButton
                    key={curr}
                    disabled={!buyingRate || !sellingData}
                    onClick={() => onValueSelect(sellingData)}
                  >
                    {currencyLoading ? (
                      <Spinner size="1rem" />
                    ) : sellingData ? (
                      formatCurrency(sellingData.amount, sellingData.currency)
                    ) : (
                      <FormattedMessage id="Global.EmDash" />
                    )}
                  </SecondaryButton>
                );
              })}
            </Row>
          </Row>
        )}
      </Col>
    </Card>
  );
};

const MarginInput = withIcon((props) => <TextInput {...props} type="number" />, {
  type: ICON_TYPE.percent,
  size: ICON_SIZE.medium,
  position: ICON_POSITION.overlay,
  order: 0,
});

const styles = {
  container: css`
    background: ${colors.grayAnatomyLight5};
    border-color: ${colors.grayAnatomyLight4};
  `,
  title: css`
    justify-content: space-between;
  `,
  form_item: css`
    width: 14rem;
  `,
};

ApplicantMarginCalculator.defaultProps = {
  onValueSelect: () => {},
};

ApplicantMarginCalculator.propTypes = {
  data: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  onValueSelect: PropTypes.func.isRequired,
};

export default ApplicantMarginCalculator;
